// Admin password policy. The backend exposes the live policy at
// GET /api/v1/auth/admin/password/policy (BLK-API-017) — fetch it with
// `usePasswordPolicy`; the defaults below are only the pre-fetch/offline fallback.
// The server's 422 `violations` remain authoritative on submit.

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "./api-client";

export interface PasswordPolicy {
  minLength: number;
  maxLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireDigit: boolean;
  requireSpecial: boolean;
}

export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 12,
  maxLength: 128,
  requireUppercase: true,
  requireLowercase: true,
  requireDigit: true,
  requireSpecial: true,
};

export function getPasswordPolicy(): Promise<PasswordPolicy> {
  return apiClient.request<PasswordPolicy>("/api/v1/auth/admin/password/policy");
}

/** Live server policy, falling back to the client defaults until it loads. */
export function usePasswordPolicy(): PasswordPolicy {
  const query = useQuery({
    queryKey: ["admin", "password-policy"],
    queryFn: getPasswordPolicy,
    staleTime: 60 * 60 * 1000, // policy changes are rare — cache for the session
    retry: 1,
  });
  return query.data ?? DEFAULT_PASSWORD_POLICY;
}

export interface PasswordRule {
  id: string;
  label: string;
  test: (value: string) => boolean;
}

export function rulesFor(policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY): PasswordRule[] {
  const rules: PasswordRule[] = [
    {
      id: "length",
      label: `At least ${policy.minLength} characters`,
      test: (v) => v.length >= policy.minLength,
    },
  ];
  if (policy.requireUppercase)
    rules.push({ id: "upper", label: "An uppercase letter", test: (v) => /[A-Z]/.test(v) });
  if (policy.requireLowercase)
    rules.push({ id: "lower", label: "A lowercase letter", test: (v) => /[a-z]/.test(v) });
  if (policy.requireDigit)
    rules.push({ id: "digit", label: "A number", test: (v) => /\d/.test(v) });
  if (policy.requireSpecial)
    rules.push({
      id: "special",
      label: "A special character",
      test: (v) => /[^A-Za-z0-9]/.test(v),
    });
  return rules;
}

export interface PasswordAssessment {
  satisfied: Record<string, boolean>;
  allMet: boolean;
  /** 0..4 strength score for the meter. */
  score: number;
  strengthLabel: "Weak" | "Fair" | "Good" | "Strong";
}

export function assessPassword(
  value: string,
  policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY,
): PasswordAssessment {
  const rules = rulesFor(policy);
  const satisfied: Record<string, boolean> = {};
  for (const r of rules) satisfied[r.id] = r.test(value);
  const allMet = rules.every((r) => satisfied[r.id]);

  // Score: variety of character classes + length bonus, capped 0..4.
  let variety = 0;
  if (/[a-z]/.test(value)) variety++;
  if (/[A-Z]/.test(value)) variety++;
  if (/\d/.test(value)) variety++;
  if (/[^A-Za-z0-9]/.test(value)) variety++;
  const lengthBonus = value.length >= policy.minLength ? 1 : value.length >= 8 ? 0 : -1;
  const score = value.length === 0 ? 0 : Math.max(0, Math.min(4, variety + lengthBonus));

  const strengthLabel =
    score >= 4 ? "Strong" : score === 3 ? "Good" : score === 2 ? "Fair" : "Weak";
  return { satisfied, allMet, score, strengthLabel };
}

/** Extract a readable message from a backend 422 password-policy problem body. */
export function policyViolationMessage(error: unknown): string | null {
  const problem = (error as { problem?: { violations?: Array<{ message?: string }> } })?.problem;
  const violations = problem?.violations;
  if (Array.isArray(violations) && violations.length > 0) {
    return violations
      .map((v) => v.message)
      .filter(Boolean)
      .join(" · ");
  }
  return null;
}
